/**
 * Utility functions for formatting data
 */

import { CURRENCY_CONFIG } from './constants';

/**
 * Format currency value with consistent USD formatting
 * @param {number} value - The currency value to format
 * @param {string} _language - Language code (ignored, kept for backward compatibility)
 * @returns {string} Formatted currency string (always USD format: $1,000.00)
 */
export const formatCurrency = (value, _language = 'en') => {
  return new Intl.NumberFormat(CURRENCY_CONFIG.CURRENCY_LOCALE, {
    style: 'currency',
    currency: CURRENCY_CONFIG.DEFAULT_CURRENCY,
    minimumFractionDigits: 2,
  }).format(value);
};

/**
 * Format date with locale-specific formatting
 * @param {string|Date} dateString - Date to format
 * @param {string} language - Language code ('en' or 'es')
 * @param {string} timeZone - IANA timezone identifier (e.g., 'America/New_York')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, language = 'en', timeZone = undefined) => {
  if (!dateString) return '-';
  
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  
  // Add timezone if provided
  if (timeZone) {
    options.timeZone = timeZone;
  }
  
  return new Date(dateString).toLocaleDateString(
    language === 'es' ? 'es-ES' : 'en-US',
    options
  );
};

/**
 * Format date and time with locale-specific formatting
 * @param {string|Date} dateString - Date to format
 * @param {string} language - Language code ('en' or 'es')
 * @param {string} timeZone - IANA timezone identifier (e.g., 'America/New_York')
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateString, language = 'en', timeZone = undefined) => {
  if (!dateString) return '-';
  
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  
  // Add timezone if provided
  if (timeZone) {
    options.timeZone = timeZone;
  }
  
  try {
    return new Date(dateString).toLocaleString(
      language === 'es' ? 'es-ES' : 'en-US',
      options
    );
  } catch (error) {
    console.error('formatDateTime error with timezone:', timeZone, error);
    
    // Fallback to UTC if timezone is invalid
    const fallbackOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    };
    
    return new Date(dateString).toLocaleString(
      language === 'es' ? 'es-ES' : 'en-US',
      fallbackOptions
    ) + ' (UTC)';
  }
};

/**
 * Format a number with thousand separators
 * @param {number} value - Number to format
 * @param {string} language - Language code ('en' or 'es')
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, language = 'en') => {
  const locale = language === 'es' ? 'es-ES' : 'en-US';
  return new Intl.NumberFormat(locale).format(value);
};

/**
 * Format percentage value
 * @param {number} value - Decimal value to format as percentage
 * @param {string} language - Language code ('en' or 'es')
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, language = 'en') => {
  const locale = language === 'es' ? 'es-ES' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format date with both language and timezone context
 * @param {string|Date} dateString - Date to format
 * @param {string} language - Language code ('en' or 'es')
 * @param {string} timeZone - IANA timezone identifier
 * @returns {string} Formatted date string
 */
export const formatDateWithContext = (dateString, language = 'en', timeZone = 'UTC') => {
  return formatDate(dateString, language, timeZone);
};

/**
 * Format date and time with both language and timezone context
 * @param {string|Date} dateString - Date to format
 * @param {string} language - Language code ('en' or 'es')
 * @param {string} timeZone - IANA timezone identifier
 * @returns {string} Formatted date and time string
 */
export const formatDateTimeWithContext = (dateString, language = 'en', timeZone = 'UTC') => {
  return formatDateTime(dateString, language, timeZone);
};